import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Check, Send, ZoomIn } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactSection from '../components/ContactSection';
import GalleryLightboxModal from '../components/GalleryLightboxModal';
import { FALLBACK_SERVICES, FALLBACK_PROJECTS, SITE_INFO } from '../data';
import { fetchCollection } from '../lib/cms';

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  
  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImgIndex, setActiveImgIndex] = useState(0);

  useEffect(() => {
    async function loadServiceData() {
      // Load services
      const cmsServices = await fetchCollection('services');
      const allServices = (cmsServices && cmsServices.length > 0) ? cmsServices : FALLBACK_SERVICES;
      
      const found = allServices.find((s) => s.url_slug === slug || s.slug === slug || s.slug?._ === slug);
      setService(found || allServices[0]);

      // Fetch dynamic gallery reference based on service_slug from CMS 'galleries' collection
      const cmsGalleries = await fetchCollection('galleries');
      if (cmsGalleries && cmsGalleries.length > 0) {
        const matchingGallery = cmsGalleries.find(
          (g) => g.service_slug === slug || g.url_slug === `${slug}-galleri`
        );
        setGallery(matchingGallery || null);
      }

      // Fetch related projects
      const cmsProjects = await fetchCollection('projects');
      const allProjects = (cmsProjects && cmsProjects.length > 0) ? cmsProjects : FALLBACK_PROJECTS;
      const matchingProjects = allProjects.filter((p) => p.service_slug === slug);
      setRelatedProjects(matchingProjects.length > 0 ? matchingProjects : allProjects.slice(0, 2));
    }

    loadServiceData();
  }, [slug]);

  if (!service) {
    return (
      <div className="py-24 text-center text-stone-600 font-heading">
        Henter ydelser...
      </div>
    );
  }

  const featuredImg = Array.isArray(service.featured_image) ? service.featured_image[0] : service.featured_image;
  const galleryImages = (gallery?.images && gallery.images.length > 0) 
    ? gallery.images 
    : (featuredImg ? [featuredImg] : []);

  return (
    <>
      <SEOHead 
        title={service.meta_title || `${service.title} - ${SITE_INFO.name}`}
        description={service.meta_description || service.short_description}
      />

      {/* Hero Header for Service */}
      <section className="bg-stone-900 text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <h1 className="font-heading font-extrabold text-3xl sm:text-5xl text-white tracking-tight">
              {service.title}
            </h1>
            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
              {service.short_description}
            </p>
            <div className="pt-4">
              <a
                href="#kontakt-sektion"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-heading font-bold text-sm px-6 py-3 rounded shadow-md transition-colors"
              >
                <span className="text-white">Få Et Uforpligtende Tilbud På {service.title}</span>
                <ArrowRight className="w-4 h-4 text-white" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Featured Photo */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-8">
              <div 
                className="prose prose-stone max-w-none text-stone-700 leading-relaxed text-base space-y-4"
                dangerouslySetInnerHTML={{ __html: service.full_description }}
              />

              {/* Service Features Checklist */}
              <div className="space-y-4 pt-2">
                <h3 className="font-heading font-bold text-stone-900 text-lg uppercase tracking-wide">
                  Hvad vi tilbyder ved {service.title.toLowerCase()}
                </h3>
                <ul className="space-y-3 text-stone-700 text-sm">
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-stone-900 font-bold shrink-0" />
                    <span>Grundig rådgivning om materialevalg og konstruktion</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-stone-900 font-bold shrink-0" />
                    <span>Gennemtænkte løsninger tilpasset dit hus og dine ønsker</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-stone-900 font-bold shrink-0" />
                    <span>Erfarne håndværkere med høj faglighed</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-stone-900 font-bold shrink-0" />
                    <span>Aftalt tidsplan og fast skriftlig pris</span>
                  </li>
                </ul>
              </div>

              {/* Direct Contact CTA */}
              <div className="pt-4 border-t border-stone-200 flex flex-wrap items-center gap-4">
                <a
                  href="#kontakt-sektion"
                  className="inline-flex items-center gap-2.5 bg-amber-700 hover:bg-amber-800 text-white font-heading font-bold text-sm px-6 py-3 shadow-sm transition-colors cursor-pointer"
                >
                  <Send className="w-4 h-4 text-white" />
                  <span>Kontakt os om {service.title.toLowerCase()}</span>
                </a>
              </div>
            </div>

            {/* Right Photo / Contact Column */}
            <div className="lg:col-span-5 space-y-6">
              {featuredImg ? (
                <div className="overflow-hidden border border-stone-200 shadow-xl">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${featuredImg.replace('https://assets.auxodev.dk/', '')}`}
                    alt={service.title}
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              ) : null}

              <div className="bg-white text-stone-900 p-6 sm:p-8 space-y-4 border border-stone-200 shadow-sm">
                <h4 className="font-heading font-bold text-lg text-stone-900">Har du spørgsmål til {service.title.toLowerCase()}?</h4>
                <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                  Kontakt os for fagmæssig rådgivning eller book et uforpligtende besigtigelsesmøde.
                </p>
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white font-medium text-sm py-3 transition-colors whitespace-nowrap"
                >
                  <Send className="w-4 h-4" />
                  <span>Send forespørgsel</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CMS Technically Reference-Linked Gallery (only shown if images exist) */}
      {galleryImages.length > 0 && (
        <section className="py-16 bg-stone-100 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-8">
            <div>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-stone-900">
                Udførte opgaver – {service.title}
              </h2>
              {gallery?.description && (
                <p className="text-stone-600 text-sm mt-1">{gallery.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setActiveImgIndex(idx);
                    setLightboxOpen(true);
                  }}
                  className="group relative h-64 overflow-hidden border border-stone-200 shadow-md cursor-pointer"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${service.title} galleri billede ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-stone-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-medium text-sm gap-2">
                    <ZoomIn className="w-5 h-5 text-amber-400" />
                    <span>Klik for stor visning</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 bg-stone-900 text-white border-t border-stone-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-8">
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
              Relaterede projekter
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedProjects.map((proj) => {
                const cover = Array.isArray(proj.cover_image) ? proj.cover_image[0] : proj.cover_image;
                return (
                  <Link
                    key={proj.url_slug || proj.id}
                    to={`/projekter/${proj.url_slug}`}
                    className="group bg-stone-950 overflow-hidden border border-stone-800 hover:border-white transition-all flex flex-col md:flex-row h-full"
                  >
                    <div className="md:w-1/2 h-56 md:h-auto relative overflow-hidden">
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${cover.replace('https://assets.auxodev.dk/', '')}`}
                        alt={proj.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="md:w-1/2 p-6 flex flex-col justify-between space-y-3">
                      <div>
                        <span className="text-xs text-white group-hover:text-white font-semibold">{proj.location}</span>
                        <h3 className="font-heading font-bold text-lg text-white group-hover:text-white transition-colors mt-1">
                          {proj.title}
                        </h3>
                        <p className="text-stone-400 text-xs line-clamp-3 mt-2">
                          {proj.description}
                        </p>
                      </div>
                      <span className="text-xs text-white font-semibold flex items-center gap-1.5 pt-2">
                        <span>Se projekt</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {galleryImages.length > 0 && (
        <GalleryLightboxModal
          images={galleryImages}
          currentIndex={activeImgIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setActiveImgIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
          onNext={() => setActiveImgIndex((prev) => (prev + 1) % galleryImages.length)}
        />
      )}

      {/* Bottom Contact Section */}
      <ContactSection title={`Indhent et uforpligtende tilbud på ${service.title.toLowerCase()}`} />
    </>
  );
}
